import React, { useEffect, useState, useCallback } from "react";
import { EmptyState, SkeletonList } from "../../components/ui";
import { Icon } from "../shared/components";
import { ensureArray, ensureObject } from "../shared/utils";

// ── Node types ────────────────────────────────────────────────────────────────
const NODE_TYPES = [
  { id: "message",      label: "Mensagem",     icon: "chat",    color: "#128C7E", desc: "Enviar uma mensagem de texto ao contato" },
  { id: "question",     label: "Pergunta",     icon: "help",    color: "#7c3aed", desc: "Fazer uma pergunta e aguardar a resposta" },
  { id: "delay",        label: "Delay",        icon: "clock",   color: "#6366f1", desc: "Aguardar N segundos antes de continuar (parece mais humano)" },
  { id: "condition",    label: "Condição",     icon: "flow",    color: "#d97706", desc: "Verificar dados e seguir caminhos diferentes" },
  { id: "webhook",      label: "Webhook",      icon: "zap",     color: "#0ea5e9", desc: "Chamar uma URL externa e usar a resposta no fluxo" },
  { id: "save_contact", label: "Salvar Dado",  icon: "contact", color: "#0d9488", desc: "Salvar uma resposta do cliente no cadastro do contato (email, empresa, notas)" },
  { id: "assign",       label: "Atribuir",     icon: "contact", color: "#2563eb", desc: "Atribuir a conversa a um atendente ou fila" },
  { id: "tag",          label: "Etiqueta",     icon: "tag",     color: "#0891b2", desc: "Adicionar etiqueta ao contato" },
  { id: "end",          label: "Encerrar",     icon: "check",   color: "#16a34a", desc: "Finalizar o fluxo do chatbot" },
];

// ── Days of week labels ───────────────────────────────────────────────────────
const DAYS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

// ── Default empty chatbot ─────────────────────────────────────────────────────
const EMPTY_CHATBOT = {
  name: "",
  description: "",
  active: false,
  is_fallback: false,
  trigger_keywords: "",
  fallback_message: "",
  business_hours_enabled: false,
  business_hours_start: "08:00",
  business_hours_end: "18:00",
  business_hours_days: "1,2,3,4,5",
  out_of_hours_message: "",
  timeout_minutes: 0,
  timeout_message: "",
  nodes: [
    { id: "start", type: "message", data: { text: "Olá! Seja bem-vindo. Como posso ajudar você?" }, next: null, options: [] },
  ],
};

let _nodeId = 1;
function genId() { return `node_${Date.now()}_${_nodeId++}`; }

// ── Node Editor ───────────────────────────────────────────────────────────────
function NodeEditor({ node, index, totalNodes, onUpdate, onDelete, onMoveUp, onMoveDown }) {
  const typeDef = NODE_TYPES.find((t) => t.id === node.type) || NODE_TYPES[0];

  function setData(key, value) {
    onUpdate({ ...node, data: { ...node.data, [key]: value } });
  }

  function addOption() {
    const options = [...(node.options || []), { label: "Opção", next: null }];
    onUpdate({ ...node, options });
  }

  function updateOption(i, key, value) {
    const options = (node.options || []).map((opt, idx) => idx === i ? { ...opt, [key]: value } : opt);
    onUpdate({ ...node, options });
  }

  function removeOption(i) {
    const options = (node.options || []).filter((_, idx) => idx !== i);
    onUpdate({ ...node, options });
  }

  return (
    <div className="chatbotNodeCard" style={{ borderLeftColor: typeDef.color }}>
      <div className="chatbotNodeHeader">
        <div className="chatbotNodeType" style={{ color: typeDef.color }}>
          <Icon name={typeDef.icon} size={13} />
          <span>{typeDef.label}</span>
          <small>{index + 1}</small>
        </div>
        <div className="chatbotNodeControls">
          {index > 0 && <button className="ghostButton" onClick={onMoveUp} title="Subir"><Icon name="chevronDown" size={12} style={{ transform: "rotate(180deg)" }} /></button>}
          {index < totalNodes - 1 && <button className="ghostButton" onClick={onMoveDown} title="Descer"><Icon name="chevronDown" size={12} /></button>}
          <button className="ghostButton danger" onClick={onDelete} title="Remover"><Icon name="trash" size={12} /></button>
        </div>
      </div>

      {/* Message */}
      {node.type === "message" && (
        <textarea
          className="chatbotNodeText"
          value={node.data?.text || ""}
          onChange={(e) => setData("text", e.target.value)}
          placeholder="Texto da mensagem. Use {name} para o nome do contato."
          rows={2}
        />
      )}

      {/* Question */}
      {node.type === "question" && (
        <div className="chatbotQuestionEditor">
          <textarea
            className="chatbotNodeText"
            value={node.data?.text || ""}
            onChange={(e) => setData("text", e.target.value)}
            placeholder="Pergunta a ser feita ao cliente"
            rows={2}
          />
          <div className="chatbotOptionsHeader">
            <small>Opções de resposta</small>
            <button className="ghostButton" onClick={addOption}><Icon name="plus" size={11} /> Opção</button>
          </div>
          <div className="chatbotOptionsList">
            {(node.options || []).map((opt, i) => (
              <div key={i} className="chatbotOptionRow">
                <span className="chatbotOptionNum">{i + 1}</span>
                <input
                  value={opt.label}
                  onChange={(e) => updateOption(i, "label", e.target.value)}
                  placeholder="Texto da opção"
                />
                <button className="ghostButton danger" onClick={() => removeOption(i)}><Icon name="close" size={11} /></button>
              </div>
            ))}
            {(!node.options || node.options.length === 0) && (
              <small className="chatbotNoOptions">Clique "+ Opção" para adicionar respostas.</small>
            )}
          </div>
        </div>
      )}

      {/* Delay */}
      {node.type === "delay" && (
        <div className="chatbotDelayEditor">
          <span>Aguardar</span>
          <input
            type="number"
            className="chatbotDelayInput"
            min={1}
            max={30}
            value={node.data?.seconds ?? 2}
            onChange={(e) => setData("seconds", Math.min(30, Math.max(1, Number(e.target.value) || 1)))}
          />
          <span>segundo(s) antes de continuar</span>
          <small style={{ color: "var(--ink-3)", display: "block", marginTop: 4 }}>
            Simula digitação humana. Máximo 30s.
          </small>
        </div>
      )}

      {/* Webhook */}
      {node.type === "webhook" && (
        <div className="chatbotWebhookEditor">
          <div className="chatbotWebhookRow">
            <select
              value={node.data?.method || "GET"}
              onChange={(e) => setData("method", e.target.value)}
              className="chatbotWebhookMethod"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
            <input
              className="chatbotWebhookUrl"
              value={node.data?.url || ""}
              onChange={(e) => setData("url", e.target.value)}
              placeholder="https://api.exemplo.com/pedido/{answer}"
            />
          </div>
          {node.data?.method === "POST" && (
            <textarea
              className="chatbotNodeText"
              rows={2}
              value={node.data?.body_template || "{}"}
              onChange={(e) => setData("body_template", e.target.value)}
              placeholder={'{"cpf": "{answer}", "empresa": "{empresa}"}'}
            />
          )}
          <input
            value={node.data?.save_fields || ""}
            onChange={(e) => setData("save_fields", e.target.value)}
            placeholder="Campos a salvar do JSON (ex: status,nome,pedido)"
            style={{ fontSize: "var(--text-xs)" }}
          />
          <input
            value={node.data?.success_message || ""}
            onChange={(e) => setData("success_message", e.target.value)}
            placeholder="Mensagem de sucesso (opcional, ex: Seu pedido está em: {status})"
            style={{ fontSize: "var(--text-xs)" }}
          />
          <input
            value={node.data?.error_message || ""}
            onChange={(e) => setData("error_message", e.target.value)}
            placeholder="Mensagem de erro (opcional, ex: Não consegui consultar. Tente novamente.)"
            style={{ fontSize: "var(--text-xs)", color: "var(--danger)" }}
          />
          <small className="chatbotWebhookHint">
            Use variáveis do fluxo na URL e no corpo: <code>{"{answer}"}</code>, <code>{"{name}"}</code>, <code>{"{empresa}"}</code> etc.
            Os campos salvos ficam disponíveis como variáveis nas próximas mensagens.
          </small>
        </div>
      )}

      {/* Save Contact */}
      {node.type === "save_contact" && (
        <div className="chatbotSaveContactEditor">
          <div className="chatbotSaveContactRow">
            <div className="chatbotSaveContactCol">
              <label>Variável do fluxo</label>
              <input
                value={node.data?.variable || "answer"}
                onChange={(e) => setData("variable", e.target.value)}
                placeholder="ex: answer, email_input"
              />
              <small>Nome da variável salva pela Pergunta anterior</small>
            </div>
            <span className="chatbotSaveContactArrow">→</span>
            <div className="chatbotSaveContactCol">
              <label>Campo do contato</label>
              <select
                value={node.data?.field || "email"}
                onChange={(e) => setData("field", e.target.value)}
              >
                <option value="email">E-mail</option>
                <option value="company">Empresa</option>
                <option value="notes">Observações</option>
              </select>
              <small>Onde salvar no cadastro</small>
            </div>
          </div>
          <small className="chatbotWebhookHint">
            Use este nó após uma <strong>Pergunta</strong>. O valor respondido pelo cliente será salvo automaticamente no CRM.
          </small>
        </div>
      )}

      {/* Condition */}
      {node.type === "condition" && (
        <div className="chatbotConditionEditor">
          <select value={node.data?.field || "message"} onChange={(e) => setData("field", e.target.value)}>
            <option value="message">Contém palavra-chave</option>
            <option value="name">Nome do contato</option>
            <option value="tag">Possui etiqueta</option>
          </select>
          <input
            value={node.data?.value || ""}
            onChange={(e) => setData("value", e.target.value)}
            placeholder="Valor para comparar"
          />
        </div>
      )}

      {/* Assign */}
      {node.type === "assign" && (
        <input
          value={node.data?.note || ""}
          onChange={(e) => setData("note", e.target.value)}
          placeholder="Nota: atribuir ao atendente disponível ou nome específico"
        />
      )}

      {/* Tag */}
      {node.type === "tag" && (
        <input
          value={node.data?.tag || ""}
          onChange={(e) => setData("tag", e.target.value)}
          placeholder="Nome da etiqueta (ex: interessado, urgente)"
        />
      )}

      {/* End */}
      {node.type === "end" && (
        <textarea
          className="chatbotNodeText"
          value={node.data?.text || "Obrigado! Em breve um atendente continuará."}
          onChange={(e) => setData("text", e.target.value)}
          placeholder="Mensagem final (opcional)"
          rows={2}
        />
      )}
    </div>
  );
}

// ── Advanced Settings Panel ───────────────────────────────────────────────────
function AdvancedSettings({ form, setField }) {
  const [open, setOpen] = useState(false);

  // parse business_hours_days CSV → Set
  const activeDays = new Set(
    String(form.business_hours_days || "1,2,3,4,5")
      .split(",").map((d) => Number(d.trim())).filter((d) => !isNaN(d))
  );

  function toggleDay(dayValue) {
    const next = new Set(activeDays);
    if (next.has(dayValue)) next.delete(dayValue);
    else next.add(dayValue);
    setField("business_hours_days", [...next].sort((a, b) => a - b).join(","));
  }

  return (
    <div className="chatbotAdvancedSettings">
      <button
        className="chatbotAdvancedToggle"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <Icon name="settings" size={13} />
        Configurações avançadas
        <span className="chatbotAdvancedChevron" style={{ transform: open ? "rotate(180deg)" : "none" }}>
          <Icon name="chevronDown" size={12} />
        </span>
      </button>

      {open && (
        <div className="chatbotAdvancedBody">

          {/* ── Horário de Atendimento ─────────────────────────── */}
          <div className="chatbotSettingsSection">
            <div className="chatbotSettingsSectionHeader">
              <label className="automationToggleLabel">
                <input
                  type="checkbox"
                  checked={!!form.business_hours_enabled}
                  onChange={(e) => setField("business_hours_enabled", e.target.checked)}
                />
                <span>Restringir ao horário de atendimento</span>
              </label>
            </div>
            {!!form.business_hours_enabled && (
              <div className="chatbotBusinessHours">
                <div className="chatbotDayPicker">
                  {DAYS.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`chatbotDayBtn${activeDays.has(d.value) ? " active" : ""}`}
                      onClick={() => toggleDay(d.value)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="chatbotTimeRange">
                  <label>
                    Das
                    <input
                      type="time"
                      value={form.business_hours_start || "08:00"}
                      onChange={(e) => setField("business_hours_start", e.target.value)}
                    />
                  </label>
                  <label>
                    às
                    <input
                      type="time"
                      value={form.business_hours_end || "18:00"}
                      onChange={(e) => setField("business_hours_end", e.target.value)}
                    />
                  </label>
                </div>
                <textarea
                  className="chatbotNodeText"
                  rows={2}
                  value={form.out_of_hours_message || ""}
                  onChange={(e) => setField("out_of_hours_message", e.target.value)}
                  placeholder="Mensagem fora do horário (ex: Nosso atendimento é de seg a sex, das 08h às 18h. Deixe sua mensagem e retornaremos em breve!)"
                />
              </div>
            )}
          </div>

          {/* ── Timeout / Inatividade ──────────────────────────── */}
          <div className="chatbotSettingsSection">
            <div className="chatbotSettingsSectionHeader">
              <strong>Timeout por inatividade</strong>
              <small>Encerra a sessão automaticamente se o contato não responder.</small>
            </div>
            <div className="chatbotTimeoutRow">
              <label>
                Encerrar após
                <input
                  type="number"
                  className="chatbotDelayInput"
                  min={0}
                  max={1440}
                  value={form.timeout_minutes || 0}
                  onChange={(e) => setField("timeout_minutes", Math.max(0, Number(e.target.value) || 0))}
                />
                minuto(s) <small style={{ color: "var(--ink-3)" }}>(0 = desativado)</small>
              </label>
            </div>
            {Number(form.timeout_minutes) > 0 && (
              <textarea
                className="chatbotNodeText"
                rows={2}
                value={form.timeout_message || ""}
                onChange={(e) => setField("timeout_message", e.target.value)}
                placeholder="Mensagem de timeout (ex: Sua sessão expirou por inatividade. Digite qualquer mensagem para começar novamente.)"
              />
            )}
          </div>

          {/* ── Fallback message ──────────────────────────────── */}
          <div className="chatbotSettingsSection">
            <div className="chatbotSettingsSectionHeader">
              <strong>Mensagem de resposta padrão</strong>
              <small>Enviada quando o contato escreve algo não reconhecido durante o fluxo.</small>
            </div>
            <textarea
              className="chatbotNodeText"
              rows={2}
              value={form.fallback_message || ""}
              onChange={(e) => setField("fallback_message", e.target.value)}
              placeholder="Ex: Não entendi sua resposta. Por favor, escolha uma das opções acima."
            />
          </div>

        </div>
      )}
    </div>
  );
}

// ── Chatbot Form (create/edit) ────────────────────────────────────────────────
function ChatbotForm({ chatbot, onSave, onClose, saving }) {
  const isNew = !chatbot?.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_CHATBOT,
    ...(chatbot || {}),
    nodes: chatbot?.nodes?.length ? chatbot.nodes : EMPTY_CHATBOT.nodes,
  }));

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function addNode(type) {
    const node = { id: genId(), type, data: {}, next: null, options: [] };
    if (type === "message")      node.data.text = "";
    if (type === "question")     node.data.text = "";
    if (type === "delay")        node.data.seconds = 2;
    if (type === "end")          node.data.text = "Obrigado! Um atendente continuará em breve.";
    if (type === "webhook")      { node.data.url = ""; node.data.method = "GET"; node.data.save_fields = ""; }
    if (type === "save_contact") { node.data.variable = "answer"; node.data.field = "email"; }
    setForm((f) => ({ ...f, nodes: [...f.nodes, node] }));
  }

  function updateNode(i, updated) {
    setForm((f) => {
      const nodes = [...f.nodes];
      nodes[i] = updated;
      return { ...f, nodes };
    });
  }

  function deleteNode(i) {
    setForm((f) => ({ ...f, nodes: f.nodes.filter((_, idx) => idx !== i) }));
  }

  function moveNode(i, dir) {
    setForm((f) => {
      const nodes = [...f.nodes];
      const j = i + dir;
      if (j < 0 || j >= nodes.length) return f;
      [nodes[i], nodes[j]] = [nodes[j], nodes[i]];
      return { ...f, nodes };
    });
  }

  return (
    <div className="chatbotEditor">
      <div className="chatbotEditorTopBar">
        <div className="chatbotEditorTitle">
          <input
            className="chatbotNameInput"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="Nome do chatbot"
          />
          <input
            value={form.trigger_keywords}
            onChange={(e) => setField("trigger_keywords", e.target.value)}
            placeholder="Palavras-chave para ativar (separadas por vírgula)"
            style={{ fontSize: "var(--text-xs)", width: 260 }}
          />
        </div>
        <div className="chatbotEditorActions">
          <label className="automationToggleLabel" title="Quando ativo, este chatbot responde a qualquer mensagem que não bater nas palavras-chave de outros chatbots">
            <input type="checkbox" checked={!!form.is_fallback} onChange={(e) => setField("is_fallback", e.target.checked)} />
            <span>Catch-all</span>
          </label>
          <label className="automationToggleLabel">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setField("active", e.target.checked)} />
            <span>Ativo</span>
          </label>
          <button onClick={() => onSave(form)} disabled={saving || !form.name.trim()}>
            {saving ? "Salvando…" : isNew ? "Criar chatbot" : "Salvar"}
          </button>
          <button className="secondaryBtn" onClick={onClose}>Cancelar</button>
        </div>
      </div>

      <div className="chatbotCanvas">
        {/* Node list */}
        <div className="chatbotFlow">
          <div className="chatbotFlowStart">
            <Icon name="zap" size={12} />
            <span>Início do fluxo</span>
            {form.trigger_keywords && <small>ativado por: {form.trigger_keywords}</small>}
          </div>
          {form.nodes.map((node, i) => (
            <React.Fragment key={node.id}>
              <div className="chatbotFlowArrow">↓</div>
              <NodeEditor
                node={node}
                index={i}
                totalNodes={form.nodes.length}
                onUpdate={(updated) => updateNode(i, updated)}
                onDelete={() => deleteNode(i)}
                onMoveUp={() => moveNode(i, -1)}
                onMoveDown={() => moveNode(i, 1)}
              />
            </React.Fragment>
          ))}
          <div className="chatbotFlowArrow">↓</div>
          <div className="chatbotAddNodeBar">
            {NODE_TYPES.map((t) => (
              <button
                key={t.id}
                className="chatbotAddNodeBtn"
                onClick={() => addNode(t.id)}
                title={t.desc}
                style={{ borderColor: t.color, color: t.color }}
              >
                <Icon name={t.icon} size={12} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview sidebar */}
        <div className="chatbotPreview">
          <div className="chatbotPreviewHeader">
            <Icon name="whatsapp" size={14} /> Prévia do fluxo
          </div>
          <div className="chatbotPreviewBody">
            {form.nodes.map((node, i) => {
              const t = NODE_TYPES.find((x) => x.id === node.type) || NODE_TYPES[0];
              return (
                <div key={node.id} className="chatbotPreviewStep">
                  <span className="chatbotPreviewStepLabel" style={{ color: t.color }}>
                    {i + 1}. {t.label}
                  </span>
                  {node.data?.text && <p>{node.data.text.replace("{name}", "Cliente")}</p>}
                  {node.type === "webhook" && (
                    <small style={{ color: "var(--ink-3)" }}>
                      🔗 {node.data?.method || "GET"} {(node.data?.url || "").slice(0, 40) || "URL não configurada"}
                      {node.data?.save_fields && <> · salva: <em>{node.data.save_fields}</em></>}
                    </small>
                  )}
                  {node.type === "save_contact" && (
                    <small style={{ color: "#0d9488" }}>
                      💾 {node.data?.variable || "answer"} → {node.data?.field === "email" ? "E-mail" : node.data?.field === "company" ? "Empresa" : "Observações"}
                    </small>
                  )}
                  {node.type === "delay" && <small style={{ color: "var(--ink-3)" }}>⏱ {node.data?.seconds ?? 2}s de pausa</small>}
                  {node.options?.length > 0 && (
                    <div className="chatbotPreviewOptions">
                      {node.options.map((opt, j) => (
                        <span key={j} className="chatbotPreviewOption">{j + 1}. {opt.label}</span>
                      ))}
                    </div>
                  )}
                  {node.data?.tag && <small>Etiqueta: {node.data.tag}</small>}
                  {node.data?.note && <small>{node.data.note}</small>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AdvancedSettings form={form} setField={setField} />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function ChatbotPage({ api }) {
  const [chatbots, setChatbots] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [botsRes, analyticsRes] = await Promise.all([
        api.get("/chatbots"),
        api.get("/chatbots/analytics").catch(() => ({ data: {} })),
      ]);
      setChatbots(ensureArray(botsRes.data));
      setAnalytics(ensureObject(analyticsRes.data));
    } catch {}
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function saveChatbot(form) {
    setSaving(true);
    setError("");
    try {
      if (form.id) {
        await api.put(`/chatbots/${form.id}`, form);
      } else {
        await api.post("/chatbots", form);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao salvar chatbot.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleChatbot(chatbot) {
    try {
      await api.patch(`/chatbots/${chatbot.id}/toggle`);
      setChatbots((prev) => prev.map((c) => c.id === chatbot.id ? { ...c, active: !c.active } : c));
    } catch {}
  }

  async function deleteChatbot(chatbot) {
    if (!window.confirm(`Excluir chatbot "${chatbot.name}"?`)) return;
    try {
      await api.delete(`/chatbots/${chatbot.id}`);
      setChatbots((prev) => prev.filter((c) => c.id !== chatbot.id));
    } catch {}
  }

  if (editing !== null) {
    return (
      <ChatbotForm
        chatbot={editing.id ? editing : null}
        onSave={saveChatbot}
        onClose={() => setEditing(null)}
        saving={saving}
      />
    );
  }

  return (
    <div className="chatbotListPage">
      <div className="automationsTopBar">
        <div>
          <h2>Chatbot Visual</h2>
          <p>Monte fluxos de atendimento automático. O chatbot responde enquanto um atendente não está disponível.</p>
        </div>
        <button onClick={() => setEditing(EMPTY_CHATBOT)}>
          <Icon name="plus" size={14} /> Novo chatbot
        </button>
      </div>

      {error && <div className="errorBanner">{error}</div>}

      {loading ? (
        <SkeletonList rows={3} />
      ) : chatbots.length === 0 ? (
        <div className="automationsEmpty">
          <EmptyState illustration="bot" title="Nenhum chatbot configurado" text="Crie um fluxo automático para atender clientes 24/7." />
          <button onClick={() => setEditing(EMPTY_CHATBOT)}>
            <Icon name="plus" size={14} /> Criar primeiro chatbot
          </button>
        </div>
      ) : (
        <div className="automationsList">
          {chatbots.map((c) => (
            <div key={c.id} className={`automationCard${c.active ? "" : " inactive"}`}>
              <div className="automationCardHead">
                <div className="automationCardInfo">
                  <strong>{c.name}</strong>
                  {c.description && <span>{c.description}</span>}
                  {c.trigger_keywords && <span>Ativado por: <em>{c.trigger_keywords}</em></span>}
                </div>
                <div className="automationCardBadges">
                  {c.is_fallback ? <span className="automationStatus on" title="Responde quando nenhum outro chatbot bater">Catch-all</span> : null}
                  {c.business_hours_enabled ? <span className="automationStatus" title="Restrito ao horário de atendimento">🕐 Horário</span> : null}
                  {Number(c.timeout_minutes) > 0 ? <span className="automationStatus" title={`Timeout: ${c.timeout_minutes}min`}>⏱ {c.timeout_minutes}min</span> : null}
                  <span className={`automationStatus${c.active ? " on" : " off"}`}>
                    {c.active ? "Ativo" : "Inativo"}
                  </span>
                  <span className="automationRuns">{(c.nodes?.length || 0)} nó(s)</span>
                </div>
                {analytics[c.id] && (
                  <div className="chatbotAnalyticsBadges">
                    <span title="Total de sessões iniciadas">📊 {analytics[c.id].total_sessions} sessões</span>
                    {analytics[c.id].completed > 0 && <span title="Concluídas">✅ {analytics[c.id].completed} concluídas</span>}
                    {analytics[c.id].handoffs > 0 && <span title="Transferidas para atendente">🤝 {analytics[c.id].handoffs} handoff</span>}
                    {analytics[c.id].timeouts > 0 && <span title="Encerradas por inatividade">⏱ {analytics[c.id].timeouts} timeout</span>}
                  </div>
                )}
              </div>
              <div className="automationCardActions">
                <button className="ghostButton" onClick={() => toggleChatbot(c)}>
                  {c.active ? "Pausar" : "Ativar"}
                </button>
                <button className="ghostButton" onClick={() => {
                  const parsed = { ...c, nodes: typeof c.nodes === "string" ? JSON.parse(c.nodes) : c.nodes };
                  setEditing(parsed);
                }}>
                  <Icon name="edit" size={12} /> Editar fluxo
                </button>
                <button className="ghostButton danger" onClick={() => deleteChatbot(c)}>
                  <Icon name="trash" size={12} /> Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChatbotPage;
